import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, Input, model, signal, TemplateRef, untracked, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { paymentAmountFromSignedOpenAmount, paymentDirectionFromOpenItems, signedOpenItemCreditAmountForDisplay, signedPaymentAmountForDisplay, toOpenItemCashFlowSource, xAdd } from '@phoenix/common';
import { CurrencyCode } from '@phx-erp/shared';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButton, SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableRowUnSelectEvent } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, lastValueFrom } from 'rxjs';
import * as dot from 'ts-dot-prop';
import { v4 as uuidv4 } from 'uuid';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { ColumnService } from '../../../../@core/services/column.service';
import {
  ColumnConfigEx,
  ColumnDefinitionEx,
  PrimeColumnDef,
} from '../../../../@core/types/column-config-types-extended';
import { numberToCurrencyString } from '../../../../@core/utils/helper';
import { Address, BalanceStatus, ColumnConfig, ColumnDefinition, ConfigOriginType, OpenItemAccountingInput, OpenItemPaymentCombined, OpenItemPaymentCombinedFilterType, OpenItemPaymentCombinedSearchInput, PaymentDebCredType, PaymentDirection, ReportType } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxInputDatepickerComponent } from '../../../../shared/components/phx-input-datepicker/phx-input-datepicker.component';
import { GenericEnumDropDownComponent } from '../../../../shared/controls/generic-enum-drop-down/generic-enum-drop-down.component';
import { ServerSettingsService } from '../../../../shared/services/server-settings.service';
import { AddressDropDownComponent } from '../../../crm/modules/company-drop-down/company-drop-down/address-drop-down.component';
import { AddressService } from '../../../crm/services/address.service';
import { OpenItemUIHelperService } from '../../../documents/services/open-item-helper.service';
import { Hotkey } from '../../../hotkeys/model/hotkey.model';
import { HotkeysService } from '../../../hotkeys/services/hotkeys.service';
import { AllowIn, ExtendedKeyboardEvent } from '../../../hotkeys/types-interfaces/hotkey.options';
import { PrintDialogViewAndEditComponent } from '../../../printing/components/print-dialog-view-and-edit/print-dialog-view-and-edit.component';
import { ReportContext } from '../../../printing/services/type-interfaces';
import { OpenItemAccountingService } from '../../services/open-item-accounting.service';
import { OpenItemHelperService } from '../../services/open-item-helper.service';
import { OpenItemPaymentCombinedService } from '../../services/open-item-payment-combined.service';
import { calculateDaysOverdue } from '../../utils/open-item-date.util';
import { BalanceInfoToPaymentListWidgetComponent } from '../balance-info-to-payment-list-widget/balance-info-to-payment-list-widget.component';
import { BalancingWidgetComponent } from '../balancing-widget/balancing-widget.component';
import {
  OpenItemArgs,
  OpenItemStatusColumnComponent,
} from '../open-item-status-column/open-item-status-column.component';
import {
  OpenItemCreditPaymentContext,
  PaymentEditWidgetComponent,
  PaymentExt,
} from '../payment-edit-widget/payment-edit-widget.component';

type OpenItemPaymentCombinedSearchInputEx = OpenItemPaymentCombinedSearchInput & {
  isPaymentTargetDateOverdue?: boolean | null;
};

export interface OpenItemFilterInfo {
  toDate?: string | null;
}

export interface OpenItemPaymentCombinedExt extends OpenItemPaymentCombined {
  overdueDays?: number | null;
}

@Component({
  selector: 'app-open-item-payment-combined-list-widget',
  templateUrl: './open-item-payment-combined-list-widget.component.html',
  standalone: true,
  imports: [
    BaseListWrapperComponent,
    ButtonModule,
    TooltipModule,
    TranslatePipe,
    SelectButtonModule,
    CheckboxModule,
    FormsModule,
    CommonModule,
    AddressDropDownComponent,
    GenericEnumDropDownComponent,
    PhxInputDatepickerComponent
  ]
})
export class OpenItemPaymentCombinedListWidgetComponent extends NewBaseListComponent<OpenItemPaymentCombined> {
  baseListService = inject(BaseListCombinedService);
  private openItemFinanceHelperService = inject(OpenItemHelperService);
  private hotkeysService = inject(HotkeysService);
  private featureHotkeys: Hotkey[] = [];

  //TODO: to implement
  // doubleClickBaseUrl = EditLinks.OPENITEMACCOUNTING;
  @Input()
  selectedOpenItemPaymentCombined: OpenItemPaymentCombined;

  currentSearchInput: OpenItemPaymentCombinedSearchInputEx = {};

  debCredOptions: any[] = [
    {
      label: this.baseListService.i18nService.translate(_('common.debitor')),
      value: 'isDebitor',
    },
    {
      label: this.baseListService.i18nService.translate(_('common.creditor')),
      value: 'isCreditor',
    },
  ];
  debCredValue: string = 'isDebitor';

  disabledBalancing: boolean = false;

  defaultSortSet: boolean = false;

  filterType = model<OpenItemPaymentCombinedFilterType>();

  toDate = model<string>(null);

  openAmountOfSelectedItemsSig = computed(() => {
    return this.selectedItems().reduce((acc, item) => acc + item.openAmount, 0);
  });

  onlyOneBalanceInfoIdInSelectedItemsSig = computed(() => {
    let balanceInfoIds = this.selectedItems().map((x) => x.balanceInfoIds);
    let uniqueBalanceInfoIds: string[] = [];
    if (balanceInfoIds.length === 0) uniqueBalanceInfoIds = Array.from(new Set(balanceInfoIds));
    if (uniqueBalanceInfoIds.length === 1) return uniqueBalanceInfoIds[0];
    else return null;
  });

  hasProcessedInSelectedItemsSig = computed(() => {
    let hasProcessed = this.selectedItems().some(
      (x) =>
        x.balanceStatus === BalanceStatus.COMPLETED ||
        x.balanceStatus === BalanceStatus.PARTIAL,
    );
    return hasProcessed;
  });

  @Input()
  isDebitor: boolean = true;
  @Input()
  isCreditor: boolean = false;

  noDebCredButtonSig = model<boolean>(false);
  defaultDocumentDecimalPlaces = signal<number>(null);

  @ViewChild('selectButton')
  selectButton: SelectButton;

  @ViewChild('debCredNumberFilter')
  debCredNumberFilter: TemplateRef<any>;

  @ViewChild('addressDropDown')
  addressDropDown: AddressDropDownComponent;

  addressFilterValue = model<Address | null>(null);

  isOpenOrPartialSig = model<boolean>(true);
  isCompletedSig = model<boolean>(false);
  isPaymentTargetDateOverdueSig = model<boolean>(false);
  useDocumentDateAsToDateSig = model<boolean>(false);

  override disabledPrintingComputedSig = computed(() => {
    return this.disabledPrintingSig();
  });

  @Input()
  ofAddress: Address;

  constructor(
    private service: OpenItemPaymentCombinedService,
    private columnService: ColumnService,
    private addressService: AddressService,
    private openItemHelperService: OpenItemUIHelperService,
    private serverSettingsService: ServerSettingsService,
    private openItemAccountingService: OpenItemAccountingService,
  ) {
    super('OpenItemPaymentCombined', service);
    this.checkboxMode = true;

    effect(() => {
      let filterType = this.filterType();
      untracked(() => {
        if (!this.initialFetchExecuted)
          return;
        this.refresh();
      });
    });
    effect(() => {
      let toDate = this.toDate();
      untracked(() => {
        if (!this.initialFetchExecuted)
          return;
        this.refresh();
      });
    });
  }

  async ngOnInit(): Promise<void> {
    this.defaultDocumentDecimalPlaces.set(
      (await this.serverSettingsService.getRoundDecimalPlaces()) ?? 0.01,
    );
    super.ngOnInit();
    this.inlineEditMode = false;
    this.addInDetail = false;
    this.noAddNew = true;
    this.noCopy = true;
    this.noBaseEdit = true;
    this.noDelete = true;
    this.noPrinter = false;
    this.setupHotkeys();
  }

  private setupHotkeys(): void {
    const hkPayment = new Hotkey(
      'f9',
      (event: ExtendedKeyboardEvent): ExtendedKeyboardEvent | boolean => {
        event.preventDefault();
        this.showNewPaymentDialog();
        return false;
      },
      [AllowIn.Input, AllowIn.Textarea, AllowIn.Select],
      this.baseListService.i18nService.translate(_('finance.hotkey-add-payment')),
    );

    const hkBalanceDespiteRest = new Hotkey(
      'f10',
      (event: ExtendedKeyboardEvent): ExtendedKeyboardEvent | boolean => {
        event.preventDefault();
        const isDisabled =
          this.openAmountOfSelectedItemsSig() === 0 ||
          !this.hasProcessedInSelectedItemsSig();
        if (!isDisabled) {
          this.balanceRestAmount();
        }
        return false;
      },
      [AllowIn.Input, AllowIn.Textarea, AllowIn.Select],
      this.baseListService.i18nService.translate(_('finance.hotkey-balance-despite-rest')),
    );

    this.featureHotkeys = [hkPayment, hkBalanceDespiteRest];
    this.featureHotkeys.forEach((hk) => this.hotkeysService.add(hk));

    this.destroyRef.onDestroy(() => {
      this.featureHotkeys.forEach((hk) => this.hotkeysService.remove(hk));
    });
  }

  protected setColumnConfigIfNeeded(colConfig: ColumnConfig): void {
    this.setDebCredNumberHeader(colConfig.columns);
    super.setColumnConfigIfNeeded(colConfig);
  }

  setDebCredNumberHeader(cols: ColumnDefinition[]) {
    for (let col of cols) {
      if (col.field === 'debCredNumber') {
        if (this.isDebitor)
          col.customHeaderName = this.baseListService.i18nService.translate(_('open-items.deb-number'));
        else if (this.isCreditor)
          col.customHeaderName = this.baseListService.i18nService.translate(_('open-items.cred-number'));
      }
    }
  }

  getSearchInputForQuery() {
    let si: OpenItemPaymentCombinedSearchInputEx = super.getSearchInputForQuery() as OpenItemPaymentCombinedSearchInputEx;
    si.isDebitor = this.isDebitor;
    si.isCreditor = this.isCreditor;
    si.isOpenOrPartial = this.isOpenOrPartialSig();
    si.isCompleted = this.isCompletedSig();
    si.isPaymentTargetDateOverdue = this.isPaymentTargetDateOverdueSig();
    si.ofAddressId = this.ofAddress?.id;
    si.useDocumentDateAsToDate = this.useDocumentDateAsToDateSig();
    si.toDate = this.toDate();
    si.filterType = this.filterType();
    let columnConfigId = this.crudOptions.currentColumnConfig?.id ?? this.currentColumnConfig?.value?.id;
    // overwrites defaultColumns if columnConfigId is not set
    if (!this.defaultSortSet && columnConfigId && !this.loadingSig()) {
      let sortMeta = [{ field: 'date', order: 2 }, { field: 'debCredNumber', order: 2 }];
      this.applyMultiSorting(this.currentSearchInput, sortMeta, ConfigOriginType.DEFAULT);

      //this is for debug a hardToFind Bug - please keep it here
      if (Object.keys(this.currentColumnConfig?.value.columns)?.length < 18)
        console.warn(
          'Failed extraMultiSort - ' +
          this.currentColumnConfig?.value.columns?.length +
          ' - id:' +
          this.currentColumnConfig?.value.id,
        );

      this.defaultSortSet = true;
    }
    return si;
  }


  onChange() {
    setTimeout(() => {
      if (
        !this.isOpenOrPartialSig() &&
        !this.isPaymentTargetDateOverdueSig() &&
        !this.isCompletedSig()
      ) {
        this.isOpenOrPartialSig.set(true);
      }
      this.refresh();
    }, 100);
  }

  onOpenOrPartialChange(isOpenOrPartial: boolean): void {
    this.isOpenOrPartialSig.set(isOpenOrPartial);
    if (isOpenOrPartial) {
      this.isPaymentTargetDateOverdueSig.set(false);
      this.isCompletedSig.set(false);
    }
    this.onChange();
  }

  onPaymentTargetDateOverdueChange(isPaymentTargetDateOverdue: boolean): void {
    this.isPaymentTargetDateOverdueSig.set(isPaymentTargetDateOverdue);
    if (isPaymentTargetDateOverdue) {
      this.isOpenOrPartialSig.set(false);
      this.isCompletedSig.set(false);
    }

    this.onChange();
  }

  onCompletedChange(isCompleted: boolean): void {
    this.isCompletedSig.set(isCompleted);
    if (isCompleted) {
      this.isOpenOrPartialSig.set(false);
      this.isPaymentTargetDateOverdueSig.set(false);
    }
    this.onChange();
  }

  onChangeUseDocumentDateAsToDate() {
    this.refresh();
  }

  async setDebCred(value: string) {
    this.debCredValue = value;

    // so the selectButton cannot be null - always one of it is chosen
    if (!this.debCredValue) {
      this.debCredValue = 'isDebitor';
      this.selectButton.writeValue(this.debCredValue);
    }
    this.isDebitor = this.debCredValue === 'isDebitor';
    this.isCreditor = this.debCredValue === 'isCreditor';
    this.currentSearchInput.isDebitor = this.debCredValue === 'isDebitor';
    this.currentSearchInput.isCreditor = this.debCredValue === 'isCreditor';
    this.setDebCredNumberHeader(this.currentColumnConfig.value.columns);
    if (this.addressDropDown)
      this.addressDropDown.clear();
    await this.setColumnConfig(this.currentColumnConfig.value);
    this.refresh();
  }

  onDoubleClickRow(rowData: OpenItemPaymentCombined, rowIndex?: number): void {
    this.showBalancingDialog(rowData);
    super.onDoubleClickRow(rowData, rowIndex);
  }

  private getCompletedOpenItems(items: OpenItemPaymentCombined[]): OpenItemPaymentCombined[] {
    return items.filter(
      (item) =>
        item.accountingType === 'OPENITEM' &&
        item.balanceStatus === BalanceStatus.COMPLETED,
    );
  }

  private uniqueBalanceInfoIds(items: OpenItemPaymentCombined[]): string[] {
    const ids = new Set<string>();
    for (const item of items) {
      for (const id of this.parseBalanceInfoIds(item.balanceInfoIds)) {
        ids.add(id);
      }
    }
    return [...ids];
  }

  private canReopenExistingBalance(items: OpenItemPaymentCombined[]): boolean {
    return this.uniqueBalanceInfoIds(items).length === 1;
  }

  isBalancingButtonDisabled(): boolean {
    const items = this.selectedItems();
    if (items.length === 0) {
      return true;
    }
    if (!this.disabledBalancing) {
      return false;
    }
    return !this.canReopenExistingBalance(items) && this.getCompletedOpenItems(items).length === 0;
  }

  async showBalancingDialog(rowData?: OpenItemPaymentCombined, completedOnly?: boolean) {
    let items: OpenItemPaymentCombined[] = [];

    if (rowData) items.push(rowData);
    else items = this.selectedItems();

    if (items.length === 0) {
      return;
    }

    const uniqueBalanceInfoIds = this.uniqueBalanceInfoIds(items);
    const completedOpenItems = this.getCompletedOpenItems(items);
    if (completedOpenItems.length > 1 && uniqueBalanceInfoIds.length !== 1) {
      this.baseListService.notificationService.error(this.baseListService.i18nService.translate(
        _('open-items.only-one-completed-open-item-for-balancing'),
      ));
      return;
    }

    const mayOpenDespiteDisabledBalancing =
      completedOnly || completedOpenItems.length === 1 || uniqueBalanceInfoIds.length === 1;
    if (this.disabledBalancing && !mayOpenDespiteDisabledBalancing) {
      return;
    }

    const openItem = items[0];

    const title = this.openItemHelperService.getTitle(openItem);

    const isReopen = uniqueBalanceInfoIds.length === 1;
    if (!isReopen && !this.openItemHelperService.balancingIsNeeded(openItem)) return;

    const ref = this.baseListService.modalService.fromComponent(BalancingWidgetComponent, {
      size: 'xl',
      verticalAlign: 'top',
      fullscreen: false,
      closable: true,
      maximizable: true,
      title: title,
      locals: {
        isDebitor: this.isDebitor,
        isCreditor: this.isCreditor,
      },
      setLocalsSig: {
        selectedOpenItemPaymentsCombinedSig: items,
      },
    });

    let sub = ref.pipe(
      catchError(err => { throw 'error in showBalancingDialog subscription: ' + err })
    ).subscribe(x => {
      if (x?.error) {
        this.baseListService.notificationService.error(x.error);
      }
      else {
        this.refresh();
        this.setSelectedItems([]);
      }
      sub.unsubscribe();
    });
  }

  openBalanceInfoToPaymentLinkDialog(payment: OpenItemPaymentCombined) {
    const ref = this.baseListService.modalService.fromComponent(
      BalanceInfoToPaymentListWidgetComponent,
      {
        size: 'md',
        verticalAlign: 'top',
        closable: true,
        maximizable: true,
        title: this.baseListService.i18nService.translate(_('finance.links-to-payment')),
        locals: {
          onlyOfPaymentId: payment.id,
        },
      },
    );
    let sub = ref
      .pipe(
        catchError((err) => {
          throw 'error in balanceInfoToPaymentLinkDialog subscription: ' + err;
        }),
      )
      .subscribe((x) => {
        this.refresh();
        this.setSelectedItems([]);
        sub.unsubscribe();
      });
  }

  private parseBalanceInfoIds(raw?: string | null): string[] {
    if (!raw) {
      return [];
    }
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  private buildOpenItemCreditPaymentContext(): OpenItemCreditPaymentContext | undefined {
    const items = this.selectedItems();
    if (!items?.length) {
      return undefined;
    }
    const ctx: OpenItemCreditPaymentContext = {};
    const firstDeb = items[0].debCredNumber;
    if (firstDeb && items.every((i) => i.debCredNumber === firstDeb)) {
      ctx.debCredNumber = firstDeb;
    }
    if (items.length === 1 && items[0].id) {
      ctx.openItemAccountingId = items[0].id;
    }
    const openItemRows = items.filter((i) => i.accountingType === 'OPENITEM' && i.id);
    if (openItemRows.length > 0) {
      // Only ids: {@link balanceDespiteRest} applies client skonto flags when booleans are sent; wrong defaults
      // (e.g. both true) override DB/view state and break overpayment + skonto booking (balancing-widget sends explicit UI flags).
      ctx.openItemAccountingsForDespiteRest = openItemRows.map((i) => ({ id: i.id }));
    }
    const idSets = items.map(
      (i) => new Set(this.parseBalanceInfoIds(i.balanceInfoIds ?? null)),
    );
    if (idSets.length > 0) {
      const intersection = [...idSets[0]].filter((id) => idSets.every((s) => s.has(id)));
      if (intersection.length === 1) {
        ctx.balanceInfoId = intersection[0];
      }
    }
    return Object.keys(ctx).length > 0 ? ctx : undefined;
  }

  async showNewPaymentDialog() {
    let onlyOfDirections: PaymentDirection[];
    let onlyOfDebCredTypes: PaymentDebCredType[];
    let onlyOfAddressId: string;
    let selectedCurrency: CurrencyCode;
    let amountToPay: number;
    const currencySet = new Set<CurrencyCode>();
    const addressIdSet = new Set<string>();
    const directionSet = new Set<PaymentDirection>();
    const debCredTypeSet = new Set<PaymentDebCredType>();
    const selectedItems = this.selectedItems();

    if (this.ofAddress && selectedItems.length === 0) {
      addressIdSet.add(this.ofAddress.id);
      if (this.ofAddress.creditorInstanceId && !this.ofAddress.debitorInstanceId) {
        directionSet.add(PaymentDirection.OUT);
        currencySet.add(this.ofAddress.creditor?.currency);
      } else if (!this.ofAddress.creditorInstanceId && this.ofAddress.debitorInstanceId) {
        directionSet.add(PaymentDirection.IN);
        currencySet.add(this.ofAddress.debitor?.currency);
      }
    }

    for (const item of selectedItems) {
      amountToPay = xAdd(amountToPay, item.openAmount ?? 0);
      if (item.debCredType === 'CREDITOR' || item.debCredType === 'DEBITOR') {
        debCredTypeSet.add(item.debCredType as PaymentDebCredType);
      }
      addressIdSet.add(item.addressId);
      currencySet.add(item.currency);
    }
    const netPaymentDirection = paymentDirectionFromOpenItems(
      selectedItems.map((item) => toOpenItemCashFlowSource(item)),
    );
    if (netPaymentDirection === 'IN' || netPaymentDirection === 'OUT') {
      directionSet.add(netPaymentDirection as PaymentDirection);
    }
    amountToPay = paymentAmountFromSignedOpenAmount(amountToPay);
    let paymentText1 = selectedItems
      .map((x) => x.identifier)
      .join(', ');

    if (directionSet.size == 1) onlyOfDirections = Array.from(directionSet);
    if (debCredTypeSet.size == 1) onlyOfDebCredTypes = Array.from(debCredTypeSet);

    if (addressIdSet.size == 1) onlyOfAddressId = Array.from(addressIdSet)[0];

    if (currencySet.size == 1) selectedCurrency = Array.from(currencySet)[0];

    let onlyOfAddress: Address;
    if (this.ofAddress) onlyOfAddress = this.ofAddress;
    if (onlyOfAddressId && !this.ofAddress)
      onlyOfAddress = await this.addressService.getItemData(onlyOfAddressId);

    const ref = this.baseListService.modalService.fromComponent(PaymentEditWidgetComponent, {
      size: 'md',
      verticalAlign: 'top',
      closable: true,
      maximizable: true,
      title: this.baseListService.i18nService.translate(_('finance.payment-edit')),
      locals: {
        onlyOfDirections: onlyOfDirections,
        onlyOfDebCredTypes: onlyOfDebCredTypes,
        address: onlyOfAddress,
        selectedCurrency: selectedCurrency,
        openItemCreditContext: this.buildOpenItemCreditPaymentContext(),
      },
      setLocalsSig: {
        paymentText1: paymentText1,
        amountToPay: amountToPay,
      },
    });

    let sub = ref
      .pipe(
        catchError((err) => {
          throw 'error in showBalancingDialog subscription: ' + err;
        }),
      )
      .subscribe((x: PaymentExt | void) => {
        void this.onPaymentEditModalClosed(x as PaymentExt | undefined).finally(() => {
          sub.unsubscribe();
        });
      });
  }

  private async onPaymentEditModalClosed(result?: PaymentExt): Promise<void> {
    this.refresh();
    if (
      result?.balancingDespiteRestInput?.balanceInfoId &&
      result.balancingDespiteRestInput.openItemAccountings?.length
    ) {
      await this.openItemFinanceHelperService.balanceDespiteRestWithBalanceInfoPreload({
        balanceInfoId: result.balancingDespiteRestInput.balanceInfoId,
        openItemAccountings: result.balancingDespiteRestInput
          .openItemAccountings as OpenItemAccountingInput[],
        payments: result.balancingDespiteRestInput.payments,
        openItemCredit: result.balancingDespiteRestInput.openItemCredit ?? undefined,
        balanceRemainingAmount: result.balancingDespiteRestInput.balanceRemainingAmount ?? false,
        onSuccess: () => {
          this.refresh();
        },
        onError: (error) => {
          console.error('balanceDespiteRest after payment edit failed', error);
        },
      });
    }
    this.setSelectedItems([]);
  }

  async balanceRestAmount() {
    await this.openItemFinanceHelperService.balanceDespiteRest({
      balanceInfoId: this.onlyOneBalanceInfoIdInSelectedItemsSig(),
      openItemAccountings: this.selectedItems() as OpenItemAccountingInput[],
      onSuccess: () => {
        this.refresh();
      },
      onError: (error) => {
        console.error('Error in balanceRestAmount:', error);
      },
    });
  }



  override getDisplayComponent(outputCell: HTMLElement, outputSpan: HTMLElement, col: PrimeColumnDef, rowData: OpenItemPaymentCombined, row: HTMLTableRowElement) {
    if (col.field === 'statusTemplate') {
      col.outputComponent = OpenItemStatusColumnComponent;
      return null;
    }

    if (outputSpan.innerHTML || !col.field) {
      return null;
    }

    const decimalPlaces: number = rowData.decimalPlaces ?? this.defaultDocumentDecimalPlaces();
    let displayValue = dot.get(rowData, col.field);
    const fieldLowCase = col.field.toLowerCase();

    if (fieldLowCase === 'documentidentifier') {
      if (outputSpan?.classList) outputSpan.classList.add('font-semibold');
    }

    if (
      fieldLowCase === 'openamount' ||
      fieldLowCase === 'accountingtype' ||
      fieldLowCase === 'address.paymentblock' ||
      fieldLowCase === 'address.dunningblock' ||
      fieldLowCase === 'address.documentblockin' ||
      fieldLowCase === 'address.documentblockout'
    ) {
      col.sortable = false;
      col.filterable = false;
    }

    if (rowData?.accountingType === 'PAYMENT') {
      if (fieldLowCase == 'amount' || fieldLowCase == 'openamount') {
        const signed = signedPaymentAmountForDisplay({
          amount: displayValue,
          direction: rowData.direction,
          debCredType: rowData.debCredType,
        });
        this.getTypeStyledDisplayValue(col, signed, outputCell, outputSpan);
        outputSpan.innerHTML = numberToCurrencyString(signed, '', decimalPlaces);
      }
    } else if (rowData?.accountingType === 'OPEN_ITEM_CREDIT') {
      if (fieldLowCase == 'amount' || fieldLowCase == 'openamount') {
        const signed = signedOpenItemCreditAmountForDisplay({
          amount: displayValue,
          direction: rowData.direction,
          debCredType: rowData.debCredType,
          documentDirection: rowData.documentDirection,
          creditType: rowData.accountingDescription,
        });
        this.getTypeStyledDisplayValue(col, signed, outputCell, outputSpan);
        outputSpan.innerHTML = numberToCurrencyString(signed, '', decimalPlaces);
      }
    }
    else {
      if (fieldLowCase.includes('amount')) {
        displayValue = this.getTypeStyledDisplayValue(col, displayValue, outputCell, outputSpan);
        displayValue = numberToCurrencyString(displayValue, '', decimalPlaces);
        outputSpan.innerHTML = displayValue;
      }
    }

    return super.getDisplayComponent(outputCell, outputSpan, col, rowData, row);
  }

  getCustomColumnArgs(col: ColumnDefinitionEx): OpenItemArgs {
    if (col.field === 'statusTemplate') {
      return {
        openBalancing: (openItem: OpenItemPaymentCombined) =>
          this.showBalancingDialog(openItem, true),
        openBalanceInfoToPaymentLinks: (openItem: OpenItemPaymentCombined) =>
          this.openBalanceInfoToPaymentLinkDialog(openItem),
      };
    }
    return {} as OpenItemArgs;
  }

  protected async setColumnConfig(colConfig: ColumnConfigEx, table?: Table) {
    if (!colConfig.columns.some((column) => column.field === 'statusTemplate')) {
      let customCol: ColumnDefinitionEx = {
        id: uuidv4(),
        dataType: 'custom',
        field: 'statusTemplate',
        customHeaderName: this.baseListService.i18nService.translate(_('open-items.status')),
        header: this.baseListService.i18nService.translate(_('open-items.status')),
        width: 90,
        visible: true,
        deletable: false,
        sortable: false,
      };

      colConfig.columns.unshift(customCol);
      try {
        await this.baseServices.columnService.saveItem(colConfig, true);
      }
      catch (e) {
        console.log("error when saving column config", e);
      }
    }

    for (let col of colConfig.columns) {
      if (col.field === 'debCredNumber') {
        // use addressDropdown as filter template
        col.filterTemplate = this.debCredNumberFilter;
      }
    }

    super.setColumnConfig(colConfig);
  }

  onRowUnselect(event: TableRowUnSelectEvent<OpenItemPaymentCombined>): void {
    const remaining = this.selectedItems();
    this.disabledBalancing = remaining.some((x) => x.openAmount === 0)
      && this.uniqueBalanceInfoIds(remaining).length !== 1;
    super.onRowUnselect(event);
  }

  override setSelectedItems(items: OpenItemPaymentCombined[]) {
    const differentDebitorsInOpenItems = new Set<string>();
    const differentCurrenciesInOpenItems = new Set<CurrencyCode>();
    const differentBalanceInfoIdsInOpenItems = new Set<string>();
    let hasError = false;

    this.disabledBalancing = false;

    // per debCredNumber right now a customer/vendor that is debitor and creditor can only be handled from debitor or creditor side at the same time not both
    for (let item of items) {
      differentDebitorsInOpenItems.add(item.debCredNumber);
      if (item.accountingType === 'OPENITEM' || item.accountingType === 'PAYMENT')
        differentCurrenciesInOpenItems.add(item.currency);
      if (item.balanceInfoIds) {
        let balanceInfoIdsArray = item.balanceInfoIds?.split(',').map((id) => id.trim());
        balanceInfoIdsArray.forEach((id) => differentBalanceInfoIdsInOpenItems.add(id));
      }
    }

    const hasZeroOpenAmount = items.some((item) => item.openAmount === 0);
    this.disabledBalancing = hasZeroOpenAmount && differentBalanceInfoIdsInOpenItems.size !== 1;

    let differentDebitorsInOpenItemsArray = Array.from(differentDebitorsInOpenItems);
    let differentCurrenciesInOpenItemsArray = Array.from(differentCurrenciesInOpenItems);
    let differentBalanceInfoIdsInOpenItemsArray = Array.from(
      differentBalanceInfoIdsInOpenItems,
    );

    if (differentDebitorsInOpenItemsArray.length > 1) {
      this.baseListService.notificationService.error(
        this.baseListService.i18nService.translate(
          _('open-items.different-debitors-in-open-items'),
        ),
      );
      hasError = true;
    }
    if (differentCurrenciesInOpenItemsArray.length > 1) {
      this.baseListService.notificationService.error(
        this.baseListService.i18nService.translate(
          _('open-items.different-currencies-in-open-items'),
        ),
      );
      hasError = true;
    }
    if (differentBalanceInfoIdsInOpenItemsArray.length > 1) {
      this.baseListService.notificationService.error(
        this.baseListService.i18nService.translate(
          _('open-items.different-balance-info-ids-in-open-items'),
        ),
      );
      hasError = true;
    }
    if (hasError) {
      items = [];
      // without the empty selection it will not deselect the wrong item
      super.setSelectedItems([]);
    }
    super.setSelectedItems(items);
  }

  /**
   * Normal print dialog (same pattern as packing-config): {@link PrintDialogViewAndEditComponent} with a {@link ReportContext}.
   * Replace {@link openItemsForPrint} when you have the final payload (e.g. enriched rows or API data).
   *
   * Note: {@link DocumentService.injectReportGenerationFunctions} is not used here — it expects a {@link Document} dataSource.
   * Add a finance-specific injector when you need report hooks / validation for open items.
   */
  override async openPrintView(): Promise<void> {
    this.disabledPrintingSig.set(true);
    try {
      const options: ReportContext = {
        dataSource: {
          openItems: null,
        },
        entityIds: null,
        /** Print dialog report dropdown: filter + create-new {@link ReportInput.entity}. */
        entityFilter: 'OpenItemPaymentCombined',
      };
      let selectedOpenItems: OpenItemPaymentCombinedExt[] = this.selectedItems();

      if (selectedOpenItems.length === 0) {
        // Same filters as the visible list (incl. open/partial, toDate, debitor/creditor, column filters).
        const searchInput = {
          ...this.getSearchInputForQuery(),
          take: 0,
          skip: 0,
        };
        const items = await lastValueFrom(
          this.service
            .getItemsByAddresses(searchInput)
            .mapSingle((x) => x.getOpenItemPaymentCombinedByAddresses.items),
        );
        selectedOpenItems = items as OpenItemPaymentCombinedExt[];
      }

      selectedOpenItems = selectedOpenItems.map((x: OpenItemPaymentCombined) => ({
        ...x,
        overdueDays: calculateDaysOverdue(x.dueDate),
      }));
      options.dataSource.openItems = selectedOpenItems;
      options.entityIds = selectedOpenItems
        .map((x: OpenItemPaymentCombinedExt) => x.id)
        .filter((id): id is string => !!id);

      let filterInfo: OpenItemFilterInfo = {
        toDate: this.toDate(),
      };

      options.dataSource.filterInfo = filterInfo;
      options.reportType = ReportType.CUSTOM;
      options.reportName = 'OpenItemPaymentCombined';

      const ref = this.baseListService.modalService
        .fromComponent(PrintDialogViewAndEditComponent, {
          closable: true,
          size: 'lg',
          height: '100vh',
          maximizable: true,
          title: this.baseListService.i18nService.translate(_('common.print')),
          setLocalsSig: {
            reportContext: options,
          },
        })
        .subscribe(() => {
          ref.unsubscribe();
        });
    } finally {
      this.disabledPrintingSig.set(false);
    }
  }

  filterPerAddress(address: Address | null, filterCallback: (value: string | null) => void): void {
    let value = this.getDebCredNumberFilterValue(address);
    filterCallback(value);
  }

  private getDebCredNumberFilterValue(address: Address | null): string | null {
    if (!address)
      return null;

    if (this.isDebitor)
      return address.debitor?.identifier ?? null;

    if (this.isCreditor)
      return address.creditor?.identifier ?? null;

    return address.debitor?.identifier ?? address.creditor?.identifier ?? null;
  }

}
